<template>
  <v-container v-if="teamCriteria">
    <v-form @submit.prevent="assignTimeLimit">
      <v-row>
        <v-col>
          <v-text-field
            v-model="teamCriteria.fieldInputTimeLimit"
            label="Time Limit (MM:SS)"
            placeholder="5:30"
            outlined
            required
            type="text"
            :rules="timeLimitRules" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="12" md="12">
          <FullWidthButton
            color="primary"
            type="submit"
            :loading="isSubmitting"
            :disabled="!isFormValid">
            Save Time Limit
          </FullWidthButton>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTeamStore, useUpdateStatusStore } from '@/stores/modules';
import { ITeamDTO, TeamDTO } from '@/dtos';
import { SNACKSTATUS } from '@/stores/util';

interface Props {
  yearId: string;
  teamId: string;
}
const statusStore = useUpdateStatusStore();
const props = defineProps<Props>();

const teamId = computed(() => props.teamId);
const yearId = computed(() => props.yearId);

const teamStore = useTeamStore();

const { data } = teamStore.getTeamForBranchYear(teamId, yearId);

const emit = defineEmits(['snack']);

const isSubmitting = ref(false);

const team = ref<ITeamDTO>(new TeamDTO());

const teamCriteria = computed(() => team.value.teamCriteria[0] ?? new TeamDTO().teamCriteria[0]);

watch(
  data,
  () => {
    team.value = new TeamDTO(data.value);
  },
  { immediate: true },
);

const isValidTime = (value: string | null | undefined) => {
  if (!value) {
    return false;
  }

  // Minutes can be any number; seconds must be 00-59
  return /^\d+:[0-5]\d$/.test(value);
};

const timeLimitRules = [
  (value: string) => !!value || 'Time limit is required',

  (value: string) => isValidTime(value) || 'Enter time as MM:SS (example: 5:30)',
];

const isFormValid = computed(() => {
  return !!teamId.value && isValidTime(teamCriteria.value?.fieldInputTimeLimit);
});

const timeToSeconds = (value: string): number => {
  const [minutes, seconds] = value.split(':').map(Number);

  return minutes * 60 + seconds;
};

const assignTimeLimit = async () => {
  if (!isFormValid.value) {
    return;
  }

  isSubmitting.value = true;

  try {
    const totalSeconds = timeToSeconds(teamCriteria.value.fieldInputTimeLimit);

    await teamStore.setTimeLimit.mutateAsync({
      teamId: teamId.value,
      yearId: yearId.value,
      timeLimit: totalSeconds,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const key = computed(() => `team-timeLimit-${teamId.value}`);

watch(
  () => statusStore.getStatus(key.value),
  (value: SNACKSTATUS | undefined) => {
    emit('snack', 'Team Time Limit', value, statusStore.getError(key.value));
  },
);
</script>
